import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { differenceInDays, parseISO, startOfDay } from 'date-fns';

interface StreakData {
  current_streak: number;
  longest_streak: number;
  last_checkin_date: string | null;
  total_checkins: number;
}

export function useCheckinStreak() {
  const queryClient = useQueryClient();

  // Fetch user
  const { data: user } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  // Fetch streak data by analysing daily_checkins
  const { data: streakData, isLoading } = useQuery({
    queryKey: ['checkin-streak', user?.id],
    queryFn: async (): Promise<StreakData> => {
      if (!user?.id) {
        return { current_streak: 0, longest_streak: 0, last_checkin_date: null, total_checkins: 0 };
      }

      // Get all check-ins ordered by date
      const { data: checkins, error } = await supabase
        .from('daily_checkins')
        .select('date')
        .eq('user_id', user.id)
        .order('date', { ascending: false });

      if (error) throw error;
      if (!checkins || checkins.length === 0) {
        return { current_streak: 0, longest_streak: 0, last_checkin_date: null, total_checkins: 0 };
      }

      const today = startOfDay(new Date());
      const lastCheckinDate = checkins[0].date;
      const lastCheckin = startOfDay(parseISO(lastCheckinDate));
      
      // Calculate current streak
      let currentStreak = 0;
      const daysSinceLastCheckin = differenceInDays(today, lastCheckin);
      
      // Only count streak if last check-in was today or yesterday
      if (daysSinceLastCheckin <= 1) {
        currentStreak = 1;
        
        // Count consecutive days
        for (let i = 1; i < checkins.length; i++) {
          const currentDate = startOfDay(parseISO(checkins[i - 1].date));
          const prevDate = startOfDay(parseISO(checkins[i].date));
          const diff = differenceInDays(currentDate, prevDate);
          
          if (diff === 1) {
            currentStreak++;
          } else {
            break;
          }
        }
      }

      // Calculate longest streak
      let longestStreak = currentStreak;
      let tempStreak = 1;
      
      for (let i = 1; i < checkins.length; i++) {
        const currentDate = startOfDay(parseISO(checkins[i - 1].date));
        const prevDate = startOfDay(parseISO(checkins[i].date));
        const diff = differenceInDays(currentDate, prevDate);
        
        if (diff === 1) {
          tempStreak++;
          longestStreak = Math.max(longestStreak, tempStreak);
        } else {
          tempStreak = 1;
        }
      }

      return {
        current_streak: currentStreak,
        longest_streak: Math.max(longestStreak, currentStreak),
        last_checkin_date: lastCheckinDate,
        total_checkins: checkins.length
      };
    },
    enabled: !!user?.id,
  });

  // Trigger streak celebration after check-in
  const celebrateMutation = useMutation({
    mutationFn: async (newStreak: number) => {
      if (!user?.id) return null;

      const { data, error } = await supabase.functions.invoke('send-streak-celebration', {
        body: {
          user_id: user.id,
          streak_type: 'daily_checkin',
          current_streak: newStreak
        }
      });

      if (error) {
        console.error('Streak celebration error:', error);
        return null;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checkin-streak'] });
    }
  });

  const checkAndCelebrate = async () => {
    // Refetch streak data after a check-in
    const result = await queryClient.fetchQuery<StreakData>({
      queryKey: ['checkin-streak', user?.id],
      staleTime: 0
    });

    if (result?.current_streak && result.current_streak > 0) {
      celebrateMutation.mutate(result.current_streak);
    }

    return result;
  };

  return {
    streak: streakData?.current_streak ?? 0,
    longestStreak: streakData?.longest_streak ?? 0,
    lastCheckinDate: streakData?.last_checkin_date,
    totalCheckins: streakData?.total_checkins ?? 0,
    isLoading,
    checkAndCelebrate,
  };
}
